import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  ForecastActivityType,
  MCDANIEL_FOLDER_ID,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FOLDER_ID,
  NOVI_SEARCH_FOLDER_NAME,
  SYSTEM_DEFAULT_FORECAST_FOLDER_ID,
  TYPE_WELLS,
  WORKING_FORECAST_FOLDER_ID,
  WORKING_FORECAST_FOLDER_NAME
} from "constants/settings.constants";

import { ForecastFolder, UserArpsItem } from "models/UserArpsModel";

import { SmartQiValues } from "components/arps/hooks/useSmartQi";

import { ICheckedForecast } from "../app/appSlice";

export const ARPS_CHART_PRODUCT_KEYS = {
  "typecurve-chart-1": "Oil + Cond",
  "typecurve-chart-2": "Oil + Cond",
  "typecurve-chart-3": "Gas",
  "typecurve-chart-4": "Gas"
};

export interface IArpsSlice {
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectCheckedForecasts) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  checkedForecasts: ICheckedForecast[];
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectCheckedTypeWells) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  checkedTypeWells: ICheckedForecast[];

  // "Global" referring to charts/other components that need to react upon changes to typewells
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectCheckedGlobalForecasts) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  checkedGlobalForecasts: ICheckedForecast[];
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectCheckedGlobalTypeWells) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  checkedGlobalTypeWells: ICheckedForecast[];
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectOriginalCheckedGlobalTypeWells) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  originalCheckedGlobalTypeWells: ICheckedForecast[]; // Backup for reset on cancel

  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectCheckedForecastKeys) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  checkedForecastKeys: string[];
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectCheckedTypeWellKeys) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  checkedTypeWellKeys: string[];

  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectExpandedForecastKeys) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  expandedForecastKeys: string[];
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectExpandedTypeWellKeys) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  expandedTypeWellKeys: string[];

  /**
   * @deprecated Folder data deferred from ForecastStore migration (EVA-6794).
   * Carries a mutable client-side draft layer (pending type wells, inline rename state, live edits)
   * that requires a Zustand draft overlay design before React Query promotion is possible.
   * Removal target: EVA-6794 implementation.
   */
  forecastFolders: ForecastFolder[];
  /**
   * @deprecated Folder data deferred from ForecastStore migration (EVA-6794).
   * See forecastFolders for details.
   */
  typeWellFolders: ForecastFolder[];

  /**
   * @deprecated Folder data deferred from ForecastStore migration (EVA-6794).
   * See forecastFolders for details. Also contains search-filter results and client-side overlays
   * (isEdit, editLocation, unsaved type well nodes, live color/segment patches).
   */
  filteredForecastFolders: ForecastFolder[];
  /**
   * @deprecated Folder data deferred from ForecastStore migration (EVA-6794).
   * See filteredForecastFolders for details.
   */
  filteredTypeWellFolders: ForecastFolder[];

  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectTemporaryForecast) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  temporaryForecast: UserArpsItem;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectIsCreatingTypeWell) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  isCreatingTypeWell: boolean;

  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectHasTypeWellSaveSucceeded) instead.
   * Removal target: next cleanup phase.
   */
  hasTypeWellSaveSucceeded: boolean;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectIsTypeWellSavable) instead.
   * Removal target: next cleanup phase.
   */
  isTypeWellSavable: boolean;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectHasUnsavedChanges) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  hasUnsavedChanges: boolean;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectSelectedTypeWell) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  selectedTypeWell: UserArpsItem;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectHasWellDataError) instead.
   * Removal target: next cleanup phase.
   */
  hasWellDataError: boolean;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectUseMcDanielForecasts) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  useMcDanielForecasts: boolean;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectUseNoviForecasts) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  useNoviForecasts: boolean;
  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectUseWorkingForecasts) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  useWorkingForecasts: boolean;
  smartQiValues: SmartQiValues;

  /**
   * @deprecated Migrated to ForecastStore (src/stores/forecast/forecastStore.ts).
   * Reads from this field should use useForecastStore(selectSyncStateType) or useForecastStore(selectSyncStateId) instead.
   * Removal target: next cleanup phase after full folder data migration.
   */
  syncState: {
    type: "typeWell" | "folder" | null;
    id: string | null;
  };
}
export const initialArpsState: IArpsSlice = {
  checkedForecasts: [],
  checkedTypeWells: [],

  checkedGlobalForecasts: [],
  checkedGlobalTypeWells: [],
  originalCheckedGlobalTypeWells: [],

  checkedForecastKeys: [MCDANIEL_FOLDER_ID],
  checkedTypeWellKeys: [],

  expandedForecastKeys: [],
  expandedTypeWellKeys: [],

  forecastFolders: [],
  typeWellFolders: [],

  filteredForecastFolders: [],
  filteredTypeWellFolders: [],

  temporaryForecast: undefined,
  isCreatingTypeWell: false,

  hasTypeWellSaveSucceeded: false,
  isTypeWellSavable: true,
  hasUnsavedChanges: false,
  selectedTypeWell: undefined,
  hasWellDataError: false,
  useMcDanielForecasts: true,
  useNoviForecasts: false,
  useWorkingForecasts: false,
  smartQiValues: {},

  syncState: {
    type: null,
    id: null
  }
};

const arpsSlice = createSlice({
  name: "arps",
  initialState: initialArpsState,
  reducers: {
    setCheckedForecasts(
      state,
      action: PayloadAction<{
        type: ForecastActivityType;
        checkedForecasts: ICheckedForecast[];
      }>
    ) {
      action.payload.type === TYPE_WELLS
        ? (state.checkedTypeWells = action.payload.checkedForecasts)
        : (state.checkedForecasts = action.payload.checkedForecasts);
    },
    setCheckedGlobalForecasts(state, action: PayloadAction<ICheckedForecast[]>) {
      state.checkedGlobalForecasts = action.payload;
    },
    setCheckedGlobalTypeWells(state, action: PayloadAction<ICheckedForecast[]>) {
      const noBackupYet = state.originalCheckedGlobalTypeWells.length === 0;
      const hasCurrentData = state.checkedGlobalTypeWells.length > 0;

      const currentIds = new Set(state.checkedGlobalTypeWells.map((tw) => tw.id));
      const newTypeWells = action.payload.filter((tw) => !currentIds.has(tw.id));

      if (noBackupYet && hasCurrentData) {
        state.originalCheckedGlobalTypeWells = [...state.checkedGlobalTypeWells];
      }
      state.checkedGlobalTypeWells = action.payload;

      // Sync color/thickness changes back to checkedTypeWells so both slices
      // stay consistent. This prevents stale colors when other code reads
      // from checkedTypeWells (e.g. during ValNav refresh recheck).
      if (state.checkedTypeWells.length > 0) {
        const globalMap = new Map(action.payload.map((tw) => [tw.id, tw]));
        state.checkedTypeWells = state.checkedTypeWells.map((tw) => {
          const global = globalMap.get(tw.id);
          if (
            global &&
            (global.color !== tw.color || global.thickness !== tw.thickness)
          ) {
            return { ...tw, color: global.color, thickness: global.thickness };
          }
          return tw;
        });
      }

      const backupIds = new Set(state.originalCheckedGlobalTypeWells.map((tw) => tw.id));
      const typeWellsToAdd = newTypeWells.filter((tw) => !backupIds.has(tw.id));

      if (typeWellsToAdd.length > 0) {
        state.originalCheckedGlobalTypeWells = [
          ...state.originalCheckedGlobalTypeWells,
          ...typeWellsToAdd
        ];
      }
    },
    setOriginalCheckedGlobalTypeWells(state, action: PayloadAction<ICheckedForecast[]>) {
      state.originalCheckedGlobalTypeWells =
        action.payload.length > 0 ? [...action.payload] : action.payload;
    },
    setCheckedKeys(
      state,
      action: PayloadAction<{
        type: ForecastActivityType;
        checkedKeys: string[];
      }>
    ) {
      action.payload.type === TYPE_WELLS
        ? (state.checkedTypeWellKeys = action.payload.checkedKeys)
        : (state.checkedForecastKeys = action.payload.checkedKeys);
    },
    setExpandedKeys(
      state,
      action: PayloadAction<{
        type: ForecastActivityType;
        expandedKeys: string[];
      }>
    ) {
      action.payload.type === TYPE_WELLS
        ? (state.expandedTypeWellKeys = action.payload.expandedKeys)
        : (state.expandedForecastKeys = action.payload.expandedKeys);
    },
    setForecastFolders(
      state,
      action: PayloadAction<{
        type: ForecastActivityType;
        forecastFolders: ForecastFolder[];
      }>
    ) {
      action.payload.type === TYPE_WELLS
        ? (state.typeWellFolders = action.payload.forecastFolders)
        : (state.forecastFolders = action.payload.forecastFolders);
    },
    setFilteredForecastFolders(
      state,
      action: PayloadAction<{
        type: ForecastActivityType;
        filteredForecastFolders: ForecastFolder[];
      }>
    ) {
      action.payload.type === TYPE_WELLS
        ? (state.filteredTypeWellFolders = action.payload.filteredForecastFolders)
        : (state.filteredForecastFolders = action.payload.filteredForecastFolders);
    },
    setTemporaryForecast(state, action: PayloadAction<UserArpsItem>) {
      state.temporaryForecast = action.payload;
    },
    setIsCreatingTypeWell(state, action: PayloadAction<boolean>) {
      state.isCreatingTypeWell = action.payload;
    },
    setHasTypeWellSaveSucceeded(state, action: PayloadAction<boolean>) {
      state.hasTypeWellSaveSucceeded = action.payload;
    },
    setIsTypeWellSavable(state, action: PayloadAction<boolean>) {
      state.isTypeWellSavable = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setSelectedTypeWell(state, action: PayloadAction<UserArpsItem>) {
      state.selectedTypeWell = action.payload;
    },
    setUseMcDanielForecasts(state, action: PayloadAction<boolean>) {
      state.useMcDanielForecasts = action.payload;
    },
    setHasWellDataError(state, action: PayloadAction<boolean>) {
      state.hasWellDataError = action.payload;
    },
    setSmartQiValues(state, action: PayloadAction<SmartQiValues>) {
      state.smartQiValues = action.payload;
    },
    setUseNoviForecasts(state, action: PayloadAction<boolean>) {
      state.useNoviForecasts = action.payload;
    },
    setUseWorkingForecasts(state, action: PayloadAction<boolean>) {
      state.useWorkingForecasts = action.payload;
    },
    setSyncStateType(state, action: PayloadAction<"typeWell" | "folder" | null>) {
      state.syncState.type = action.payload;
    },
    setSyncStateId(state, action: PayloadAction<string | null>) {
      state.syncState.id = action.payload;
    },
    resetArpsSlice: () => initialArpsState
  }
});

export function getSpecialFolders(
  mcdaniel: boolean,
  novi: boolean,
  working: boolean
): string[] {
  const folders = [];
  if (mcdaniel) {
    folders.push(MCDANIEL_SEARCH_FOLDER_NAME);
  }
  if (working) {
    folders.push(WORKING_FORECAST_FOLDER_NAME);
  }
  if (novi) {
    folders.push(NOVI_SEARCH_FOLDER_NAME);
  }
  return folders;
}

export type SPECIAL_FOLDERS_TYPE =
  | typeof MCDANIEL_FOLDER_ID
  | typeof WORKING_FORECAST_FOLDER_ID
  | typeof NOVI_FOLDER_ID;

/** Value of the single-valued defaultForecastSource setting. A superset of
 *  SPECIAL_FOLDERS_TYPE: "-4" (System Default) is a setting value, never a tree node. */
export type FORECAST_SOURCE_SETTING_TYPE =
  | SPECIAL_FOLDERS_TYPE
  | typeof SYSTEM_DEFAULT_FORECAST_FOLDER_ID;

export const SPECIAL_FOLDERS_ID: string[] = [
  MCDANIEL_FOLDER_ID,
  WORKING_FORECAST_FOLDER_ID,
  NOVI_FOLDER_ID
];

export const {
  setCheckedForecasts,
  setCheckedGlobalForecasts,
  setCheckedGlobalTypeWells,
  setOriginalCheckedGlobalTypeWells,
  setCheckedKeys,
  setExpandedKeys,
  setFilteredForecastFolders,
  setTemporaryForecast,
  setIsCreatingTypeWell,
  setForecastFolders,
  setHasTypeWellSaveSucceeded,
  setIsTypeWellSavable,
  setHasUnsavedChanges,
  setSelectedTypeWell,
  setUseMcDanielForecasts,
  setHasWellDataError,
  setUseNoviForecasts,
  setUseWorkingForecasts,
  setSmartQiValues,
  setSyncStateType,
  setSyncStateId,
  resetArpsSlice
} = arpsSlice.actions;
export default arpsSlice.reducer;
